import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ShoppingCart, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';

const playCartSound = () => {
  const audio = new Audio('data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYZNLBruAAAAAAD/+1DEAAAGAAGn9AAAIwwKd/OMACQAABpAAAABQGCAIAh8EAQBAH/BAEAQBA4sIAgCAIHc+ZQEAXP/6gIAgCAJnn/pAEPBA1/84CgIAgGOD//lwGBsMsP/nkQGPgg+f+8MYgIJn+MMDAYGBQM8H//+DBg4f//5wcGIBAED///+UGIIgCDoGJgcf////h+c/////BgxiRq7/2AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//tQxAADwAABpAAAACAAADSAAAAE//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////8=');
  audio.volume = 0.3;
  audio.play().catch(() => {});
};

const FitnessSupplements = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [addedItems, setAddedItems] = useState({});
  const [showPopup, setShowPopup] = useState(false);

  const products = [
    {
      id: 'creatine',
      title: 'Creatine Monohydrate',
      subtitle: 'קראטין',
      price: 149,
      image: 'https://customer-assets.emergentagent.com/job_intimate-care-pro-1/artifacts/bx8c5kgv_cf2c16_70d03ddcda3c4df7bd01806d42c7ff14~mv2.avif'
    },
    {
      id: 'sparta',
      title: 'קדם אימון SPARTA',
      subtitle: 'Pre-Workout',
      price: 249,
      image: 'https://customer-assets.emergentagent.com/job_intimate-care-pro-1/artifacts/k0gb1hz1_cf2c16_60e12e73b274416cb3f65c591a08358f~mv2.avif'
    },
    {
      id: 'glutamine',
      title: 'L-Glutamine',
      subtitle: 'ל-גלוטמין',
      price: 129,
      image: 'https://customer-assets.emergentagent.com/job_intimate-care-pro-1/artifacts/0wfnu2ej_3.png'
    }
  ];

  const handleButtonClick = (product) => {
    if (addedItems[product.id]) {
      navigate('/cart');
    } else {
      addToCart({
        id: `supplement-${product.id}`,
        name: product.title,
        description: product.subtitle,
        price: product.price,
        image: product.image,
        quantity: 1
      });
      
      playCartSound();
      setAddedItems(prev => ({ ...prev, [product.id]: true }));
      setShowPopup(true);
      
      setTimeout(() => {
        setShowPopup(false);
      }, 2000);
    }
  };

  return (
    <div className="container py-8" data-testid="fitness-supplements-page">
      {/* Popup */}
      {showPopup && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-green-500 text-white px-8 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-bounce">
          <Check className="w-6 h-6" />
          <span className="font-bold text-lg" style={{ fontFamily: 'Heebo, sans-serif' }}>נוסף לסל</span>
        </div>
      )}

      {/* Back Button */}
      <button
        onClick={() => navigate('/fitness')}
        className="flex items-center gap-2 text-[#2C3E2D] hover:text-[#D97706] transition-colors mb-8"
        data-testid="back-button"
      >
        <ArrowRight className="w-5 h-5" />
        <span className="font-medium">חזרה למוצרים</span>
      </button>

      {/* Title */}
      <h1 className="text-4xl md:text-5xl font-bold text-[#2C3E2D] text-center mb-10" style={{ fontFamily: 'Heebo, sans-serif' }}>
        תוספים
      </h1>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {products.map((product) => (
          <div
            key={product.id}
            className="bg-white rounded-2xl shadow-lg overflow-hidden border border-stone-200 hover:shadow-xl transition-all duration-300"
            data-testid={`product-${product.id}`}
          >
            {/* Image */}
            <div className="p-4 bg-gradient-to-b from-stone-100 to-white flex items-center justify-center rounded-t-2xl" style={{ minHeight: '200px' }}>
              <img
                src={product.image}
                alt={product.title}
                className="max-w-full max-h-48 object-contain mix-blend-multiply"
              />
            </div>

            {/* Content */}
            <div className="px-4 py-3 text-center">
              <h3 className="text-lg font-bold text-[#2C3E2D]" style={{ fontFamily: 'Heebo, sans-serif' }}>
                {product.title}
              </h3>
              <p className="text-sm text-stone-500">{product.subtitle}</p>
            </div>

            {/* Price and Button */}
            <div className="p-4 bg-stone-50 text-center">
              <div className="text-2xl font-bold text-[#D97706] mb-3" style={{ fontFamily: 'Heebo, sans-serif' }}>
                {product.price} ש"ח
              </div>
              <button
                onClick={() => handleButtonClick(product)}
                className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-lg transition-all duration-300 ${
                  addedItems[product.id]
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-[#D97706] text-white hover:bg-[#B45309]'
                } hover:shadow-lg`}
                style={{ fontFamily: 'Heebo, sans-serif' }}
              >
                <ShoppingCart className="w-5 h-5" />
                {addedItems[product.id] ? 'מעבר לרכישה' : 'הוסף לסל'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FitnessSupplements;
